import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix } from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileSignature, Plus, Search, X, Eye, Edit, Trash2, Filter,
  ChevronDown, ChevronUp, Download, Upload, FileText,
  FileSpreadsheet, FileOutput, List, LayoutList, LayoutGrid,
  Shield, Layers, RefreshCcw, CheckSquare, Square,
  CheckCircle, XCircle, Tag,
  Globe, Hash, Star, AlignLeft, Stamp
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import localApiAdapter from '../../core/services/localApiAdapter';
import { CustomSelect } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { sliceQawaemStats } from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const ModelesMentionsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.modelesMentionsPage.${key}`, opts);

  const TEMPLATE_KIND_OPTIONS = [
    { value: 'mention', label: p('kind_mention') },
    { value: 'modèle', label: p('kind_template') },
    { value: 'pied', label: p('kind_footer') },
    { value: 'filigrane', label: p('kind_watermark') }
  ];

  const LOCALE_OPTIONS = [
    { value: 'fr-DZ', label: p('locale_fr_dz') },
    { value: 'fr', label: p('locale_fr') },
    { value: 'ar', label: p('locale_ar') },
    { value: 'en', label: p('locale_en') }
  ];

  const STATUS_OPTIONS = [
    { value: 'active', label: p('status_active') },
    { value: 'inactive', label: p('status_inactive') }
  ];
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  useFacturationModalBodyLock(showModal, showViewModal);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_templates');
  const [quickFilter, setQuickFilter] = useState('all');

  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  const [mentionFormTab, setMentionFormTab] = useState('identity');

  const [formData, setFormData] = useState({
    template_number: '',
    name: '',
    template_kind: '',
    locale: '',
    content: '',
    variables_json: '',
    is_default: '',
    status: '',
    notes: ''
  });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get('/facturation/modeles-mentions');
      const data = response?.data ?? response;
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading legal templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      window.alert(p('alert_name_required'));
      return;
    }
    try {
      setLoading(true);
      const payload = {
        ...formData,
        template_kind: formData.template_kind || 'mention',
        locale: formData.locale || 'fr-DZ',
        is_default: formData.is_default === '1' || formData.is_default === 1 || formData.is_default === true ? 1 : 0,
        status: formData.status || 'active'
      };
      if (selectedRecord) {
        await localApiAdapter.put(`/facturation/modeles-mentions/${selectedRecord.id}`, payload);
      } else {
        await localApiAdapter.post('/facturation/modeles-mentions', payload);
      }
      await loadTemplates();
      resetForm();
    } catch (error) {
      console.error('Error saving legal template:', error);
      window.alert(error?.response?.data?.error || p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      template_number: record.template_number ?? '',
      name: record.name ?? '',
      template_kind: record.template_kind ?? '',
      locale: record.locale ?? '',
      content: record.content ?? '',
      variables_json: record.variables_json ?? '',
      is_default: record.is_default != null ? String(record.is_default) : '',
      status: record.status ?? '',
      notes: record.notes ?? ''
    });
    setMentionFormTab('identity');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('confirm_delete'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`/facturation/modeles-mentions/${id}`);
        await loadTemplates();
      } catch (error) {
        console.error('Error deleting legal template:', error);
        window.alert(error?.response?.data?.error || p('alert_delete_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleView = (record) => {
    setSelectedRecord(record);
    setShowViewModal(true);
  };

  const statusOptions = useMemo(() => [
    { value: 'active', label: p('status_active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'inactive', label: p('status_inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/modeles-mentions/${id}`, { status: newStatus });
      await loadTemplates();
    } catch (error) {
      console.error('Error updating template status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      template_number: '',
      name: '',
      template_kind: '',
      locale: '',
      content: '',
      variables_json: '',
      is_default: '',
      status: '',
      notes: ''
    });
    setSelectedRecord(null);
    setShowModal(false);
    setMentionFormTab('identity');
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('confirm_bulk_delete', { count: selectedRecords.length }))) {
      try {
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/facturation/modeles-mentions/${id}`)));
        setSelectedRecords([]);
        loadTemplates();
      } catch (error) {
        console.error('Error bulk deleting:', error);
      }
    }
  };

  const stats = {
    total: templates.length,
    actifs: templates.filter(t => t.status === 'active').length,
    mentions: templates.filter(t => t.template_kind === 'mention').length,
    modeles: templates.filter(t => t.template_kind === 'modèle').length,
    parDefaut: templates.filter(t => t.is_default === 1).length
  };

  const templateDistribution = {
    all_templates: {
      title: p('dist_overview_title'),
      data: [
        { name: p('dist_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('stat_mentions'), count: stats.mentions, color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { name: p('stat_templates'), count: stats.modeles, color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        { name: p('stat_default'), count: stats.parDefaut, color: 'bg-gradient-to-r from-amber-500 to-amber-600' }
      ]
    },
    par_type: {
      title: p('dist_by_type_title'),
      data: TEMPLATE_KIND_OPTIONS.map((k, i) => ({
        name: k.label,
        count: templates.filter(t => t.template_kind === k.value).length,
        color: [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-cyan-500 to-cyan-600',
          'bg-gradient-to-r from-slate-500 to-slate-600'
        ][i]
      }))
    },
    par_statut: {
      title: p('dist_by_status_title'),
      data: [
        { name: p('dist_active'), count: stats.actifs, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { name: p('dist_inactive'), count: templates.filter(t => t.status === 'inactive').length, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    }
  };

  const distributionInfo = templateDistribution[mainViewMode] || templateDistribution.all_templates;

  const mainTabs = [
    { id: 'all_templates', label: p('tab_general'), icon: LayoutGrid },
    { id: 'par_type', label: p('tab_by_type'), icon: FileSignature },
    { id: 'par_statut', label: p('tab_by_status'), icon: Shield }
  ];

  const quickFilterButtons = [
    { id: 'all', label: p('filter_all_types'), icon: Layers },
    { id: 'mention', label: p('filter_mentions'), icon: AlignLeft },
    { id: 'modèle', label: p('filter_templates'), icon: FileText },
    { id: 'pied', label: p('filter_footers'), icon: Stamp },
    { id: 'filigrane', label: p('filter_watermarks'), icon: Tag }
  ];

  const columnFilterRules = useMemo(() => [
    textColumnRule('template_number', (row) => row.template_number),
    textColumnRule('name', (row) => row.name),
    exactColumnRule('template_kind', (row) => row.template_kind),
    textColumnRule('locale', (row) => row.locale),
  ], []);

  const filteredTemplates = useMemo(() => {
    const base = templates.filter((record) => {
      const matchesFilter = quickFilter === 'all' || record.template_kind === quickFilter;
      const matchesSearch = !searchTerm ||
        record.template_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.locale?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesFilter && matchesSearch;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [templates, searchTerm, quickFilter, filters, columnFilterRules]);

  const {
    facturationConfig,
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredTemplates);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  useEffect(() => {
    if (viewMode === 'table' && String(searchTerm || '').trim()) {
      setSearchTerm('');
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredRecordIds = useMemo(() => filteredTemplates.map((r) => r.id), [filteredTemplates]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredRecordIds);
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const getKindBadge = (kind) => {
    const styles = {
      mention: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'modèle': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      pied: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      filigrane: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
    };
    const labels = {
      mention: p('kind_badge_mention'),
      'modèle': p('kind_badge_template'),
      pied: p('kind_badge_footer'),
      filigrane: p('kind_badge_watermark')
    };
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${styles[kind] || 'bg-white/10 text-white/70 border-white/20'}`}>
        {labels[kind] || kind || p('na')}
      </span>
    );
  };

  const truncateContent = (text, max = 80) => {
    if (!text) return '—';
    return text.length > max ? `${text.slice(0, max)}…` : text;
  };

  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'template_number', label: p('col_template_number'), required: true },
    { key: 'name', label: p('col_name') },
    { key: 'template_kind', label: p('col_type') },
    { key: 'locale', label: p('col_locale') },
    { key: 'content', label: p('col_content') },
    { key: 'is_default', label: p('col_default') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol),
    [qawaemBaseColumns, showCol],
  );


  const mentionFormTabs = useMemo(() => ([
    { id: 'identity', label: p('section_identity'), icon: Tag, iconColor: 'text-blue-400' },
    { id: 'content', label: p('section_content'), icon: FileText, iconColor: 'text-green-400' },
  ]), [p]);

  return (
    <div className="space-y-6 employees-container">
      <div className="flex justify-between items-start list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <FileSignature className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={loadTemplates} className="refresh-button" title={p('refresh')}>
              <RefreshCcw size={20} />
            </button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
            className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
          >
            <Filter className="h-5 w-5" />
            <span className="hidden sm:inline">{p('advanced_options')}</span>
            {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>{p('new_template')}</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {sliceQawaemStats([
          { label: p('stat_total'), value: stats.total, icon: FileSignature, color: 'from-blue-500 to-blue-600' },
          { label: p('stat_active'), value: stats.actifs, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { label: p('stat_mentions'), value: stats.mentions, icon: AlignLeft, color: 'from-cyan-500 to-cyan-600' },
          { label: p('stat_templates'), value: stats.modeles, icon: FileText, color: 'from-purple-500 to-purple-600' },
          { label: p('stat_default'), value: stats.parDefaut, icon: Star, color: 'from-amber-500 to-amber-600' }
        ]).map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="relative group"
          >
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm font-medium">{stat.label}</p>
                  <p className="text-2xl font-bold text-white force-white mt-2">{stat.value}</p>
                </div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} style={{ pointerEvents: 'none' }} />
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white force-white" />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <AnimatePresence>
        {showAdvancedOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 space-y-6 overflow-hidden"
          >
            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('main_view')}</h3>
              <div className="flex flex-wrap gap-2">
                {mainTabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setMainViewMode(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                      mainViewMode === tab.id
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30'
                        : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <tab.icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{p('quick_filters')}</h3>
              <div className="flex flex-wrap gap-2">
                {quickFilterButtons.map(btn => (
                  <button
                    key={btn.id}
                    onClick={() => setQuickFilter(btn.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      quickFilter === btn.id
                        ? 'bg-purple-500/30 text-purple-200 border-2 border-purple-400/50'
                        : 'bg-white/5 text-white/60 hover:bg-white/10'
                    }`}
                  >
                    <btn.icon className="h-4 w-4" />
                    {btn.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-white/80 text-sm font-semibold mb-3 uppercase tracking-wider">{distributionInfo.title}</h3>
              <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
                {distributionInfo.data.map((item, index) => {
                  const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                  const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0;
                  return (
                    <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex justify-between items-center mb-2">
                        <span className="block w-full text-white/70 text-sm font-medium text-right">{item.name}</span>
                        <span className="text-white font-semibold">{item.count} ({percentage}%)</span>
                      </div>
                      <div className="h-2.5 bg-white/5 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${percentage}%` }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className={`h-full ${item.color} rounded-full`}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between" style={{ overflow: 'visible' }}>
            {viewMode === 'cards' ? (
              <div className="flex-1 relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={p('search_placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            ) : null}

            <div className="flex items-center gap-2 flex-wrap ml-auto" style={{ overflow: 'visible' }}>
              <div className="flex items-center gap-0">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-2 border border-white/20 transition-all ${
                    viewMode === 'cards' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-white/5 text-white hover:text-white/90 hover:border-white/30'
                  } rounded-l-lg border-r-0`}
                  title={p('view_cards')}
                >
                  <LayoutList size={18} />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`px-3 py-2 border border-white/20 transition-all ${
                    viewMode === 'table' ? 'bg-blue-500/20 text-blue-300 border-blue-500/30' : 'bg-white/5 text-white hover:text-white/90 hover:border-white/30'
                  } rounded-r-lg`}
                  title={p('view_table')}
                >
                  <List size={18} />
                </button>
              </div>

              {selectedRecords.length > 0 ? (
                <button
                  type="button"
                  onClick={handleBulkDelete}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
                >
                  <Trash2 size={16} />
                  <span>{p('delete_with_count', { count: selectedRecords.length })}</span>
                </button>
              ) : null}

              <div className="relative" ref={importDropdownRef}>
                <button
                  onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }}
                  className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Upload size={16} />
                  <span className="text-sm font-medium">{p('import')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="absolute top-full mt-2 right-0 border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px] z-[99999] bg-slate-800">
                    <div className="p-2">
                      {[{ key: 'CSV', label: t('common.format_csv') }, { key: 'Excel', label: t('common.format_excel') }, { key: 'JSON', label: t('common.format_json') }].map(({ key: fmt, label }, i) => (
                        <button key={fmt} onClick={() => setShowImportDropdown(false)} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                          {i === 0 && <FileText size={16} className="text-red-400" />}
                          {i === 1 && <FileSpreadsheet size={16} className="text-green-400" />}
                          {i === 2 && <FileOutput size={16} className="text-purple-400" />}
                          <span className="text-sm font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={exportDropdownRef}>
                <button
                  onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }}
                  className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5"
                >
                  <Download size={16} />
                  <span className="text-sm font-medium">{p('export')}</span>
                  <ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="absolute top-full mt-2 right-0 border border-blue-400/60 rounded-2xl shadow-2xl min-w-[120px] z-[99999] bg-slate-800">
                    <div className="p-2">
                      {[{ key: 'PDF', label: t('common.format_pdf') }, { key: 'Excel', label: t('common.format_excel') }, { key: 'CSV', label: t('common.format_csv') }].map(({ key: fmt, label }, i) => (
                        <button key={fmt} onClick={() => setShowExportDropdown(false)} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 transition-colors flex items-center gap-3 rounded-xl">
                          {i === 0 && <FileText size={16} className="text-blue-400" />}
                          {i === 1 && <FileSpreadsheet size={16} className="text-green-400" />}
                          {i === 2 && <FileOutput size={16} className="text-purple-400" />}
                          <span className="text-sm font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="p-8 text-center text-white/60">{p('loading')}</div>
        )}

        {viewMode === 'table' && !loading && (
          <div className="overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="bg-white/5">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('template_number') ? <TableColumnHeaderFilter columnKey="template_number" label={p('col_template_number')} value={filters.template_number || ''} onChange={(v) => setFilter('template_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('name') ? <TableColumnHeaderFilter columnKey="name" label={p('col_name')} value={filters.name || ''} onChange={(v) => setFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('template_kind') ? <TableColumnHeaderFilter columnKey="template_kind" label={p('col_type')} kind="select" options={TEMPLATE_KIND_OPTIONS} value={filters.template_kind || ''} onChange={(v) => setFilter('template_kind', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('locale') ? <TableColumnHeaderFilter columnKey="locale" label={p('col_locale')} kind="select" options={LOCALE_OPTIONS} value={filters.locale || ''} onChange={(v) => setFilter('locale', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('content') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_content')}</th> : null}
                  {showCol('is_default') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_default')}</th> : null}
                  {showCol('status') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredTemplates.length > 0 ? pagedItems.map((record, index) => (
                  <tr
                    key={record.id}
                    className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}
                  >
                    <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                    {showCol('template_number') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold font-mono">{record.template_number}</span>
                      </td>
                    ) : null}
                    {showCol('name') ? (
                      <td className="px-6 py-4 text-white font-semibold">{record.name}</td>
                    ) : null}
                    {showCol('template_kind') ? (
                      <td className="px-6 py-4">{getKindBadge(record.template_kind)}</td>
                    ) : null}
                    {showCol('locale') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/70 text-sm flex items-center gap-1">
                          <Globe size={14} className="text-blue-400" />
                          {record.locale || 'fr-DZ'}
                        </span>
                      </td>
                    ) : null}
                    {showCol('content') ? (
                      <td className="px-6 py-4 text-white/60 text-sm max-w-xs">{truncateContent(record.content)}</td>
                    ) : null}
                    {showCol('is_default') ? (
                      <td className="px-6 py-4 text-center">
                        {record.is_default === 1 ? (
                          <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 inline-flex items-center gap-1">
                            <Star size={14} /> {p('default_badge')}
                          </span>
                        ) : (
                          <span className="text-white/40 text-xs">—</span>
                        )}
                      </td>
                    ) : null}
                    {showCol('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        </div>
                      </td>
                    ) : null}
                    <td className="px-6 py-4">
                      <QawaemErpRowActions
                        onView={() => handleView(record)}
                        onEdit={() => handleEdit(record)}
                        onDelete={() => handleDelete(record.id)}
                        viewTitle={p('view')}
                        editTitle={p('edit')}
                        deleteTitle={p('delete')}
                      />
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={visibleTableColCount} className="text-center py-12 text-white/60">
                      <FileSignature size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm text-white/40 mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && !loading && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredTemplates.length > 0 ? pagedItems.map((record, index) => (
              <motion.div
                key={record.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center flex-wrap gap-3 mb-4">
                      <h3 className="text-xl font-bold text-white flex items-center gap-2">
                        <FileSignature size={20} className="text-blue-400" />
                        {record.name}
                      </h3>
                      {getKindBadge(record.template_kind)}
                      {record.is_default === 1 && (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                          <Star size={12} /> {p('default_for_type')}
                        </span>
                      )}
                      <div className="flex"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm text-white/70">
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_identity')}</h5>
                        <div className="space-y-1">
                          <div><span className="font-medium">{p('label_number_short')}</span> {record.template_number}</div>
                          <div><span className="font-medium">{p('label_name_short')}</span> {record.name}</div>
                          <div className="flex items-center gap-1"><Globe size={14} /><span className="font-medium">{p('label_locale_short')}</span> {record.locale || 'fr-DZ'}</div>
                        </div>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_content')}</h5>
                        <p className="text-white/60 text-xs leading-relaxed">{truncateContent(record.content, 200)}</p>
                      </div>
                      <div>
                        <h5 className="font-medium text-white mb-2">{p('section_configuration')}</h5>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2"><span className="font-medium">{p('label_status_short')}</span> <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                          {record.variables_json && (
                            <div className="pt-1 border-t border-white/10 text-xs text-amber-300">{p('variables_defined')}</div>
                          )}
                          {record.notes && <div className="pt-1 text-xs italic">{record.notes}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => handleView(record)}
                    onEdit={() => handleEdit(record)}
                    onDelete={() => handleDelete(record.id)}
                    viewTitle={p('view')}
                    editTitle={p('edit')}
                    deleteTitle={p('delete')}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="col-span-full text-center py-12">
                <FileSignature size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_title')}</p>
              </div>
            )}
          </div>
        )}
        <FacturationListPaginationBar
          totalItems={facturationListTotal}
          rawPage={facturationListPage}
          onPageChange={setFacturationListPage}
          pageSize={facturationPageSize}
        />
      </div>

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={() => setShowModal(false)}
          title={selectedRecord ? p('edit_template') : p('new_template')}
          icon={FileSignature}
          formId="modeles-mentions-form"
          saveLabel={selectedRecord ? p('save') : p('create')}
          onSubmit
          testId="modeles-mentions-modal"
          tabs={mentionFormTabs}
          activeTab={mentionFormTab}
          onTabChange={setMentionFormTab}
        >
              <form id="modeles-mentions-form" onSubmit={handleSubmit}>
                {mentionFormTab === 'identity' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_number')}</label>
                            <input type="text" value={formData.template_number} onChange={(e) => setFormData({ ...formData, template_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_auto_number')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_name')} required /></label>
                            <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_name_example')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_type')}</label>
                            <CustomSelect value={formData.template_kind} onChange={(val) => setFormData({ ...formData, template_kind: val })} options={TEMPLATE_KIND_OPTIONS.map(k => ({ ...k, isDefault: true }))} placeholder={p('placeholder_kind')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_locale')}</label>
                            <CustomSelect value={formData.locale} onChange={(val) => setFormData({ ...formData, locale: val })} options={LOCALE_OPTIONS.map(l => ({ ...l, isDefault: true }))} placeholder={p('placeholder_language')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_status')}</label>
                            <CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val })} options={STATUS_OPTIONS.map(s => ({ ...s, isDefault: true }))} placeholder={p('placeholder_status')} searchable />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_default')}</label>
                            <CustomSelect
                              value={formData.is_default}
                              onChange={(val) => setFormData({ ...formData, is_default: val })}
                              options={[
                                { value: '0', label: p('no'), isDefault: true },
                                { value: '1', label: p('yes'), isDefault: true }
                              ]}
                              placeholder={p('placeholder_default_for_type')}
                              searchable
                            />
                          </div>
                        </div>
                )}

                {mentionFormTab === 'content' && (
                  <div className="space-y-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_content_text')}</label>
                            <textarea
                              value={formData.content}
                              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                              rows={6}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                              placeholder={p('placeholder_content')}
                            />
                            <p className="text-white/40 text-xs mt-2">{p('variable_hint', { syntax: p('variable_syntax') })}</p>
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_variables')}</label>
                            <textarea
                              value={formData.variables_json}
                              onChange={(e) => setFormData({ ...formData, variables_json: e.target.value })}
                              rows={3}
                              className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder='["tva_rate","company_nif","client_name"]'
                            />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('section_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}

      {showViewModal && selectedRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('details_prefix', { name: selectedRecord.name })}
          icon={FileSignature}
          maxWidth="1100px"
          testId="modeles-mentions-view-modal"
        >
            <div className="space-y-4">
              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <Hash size={18} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_identity')}</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_template_number')}</p><p className="text-white font-semibold font-mono">{selectedRecord.template_number}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_name')}</p><p className="text-white font-semibold">{selectedRecord.name}</p></div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_type')}</p>{getKindBadge(selectedRecord.template_kind)}</div>
                  <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_locale')}</p><p className="text-white font-semibold">{selectedRecord.locale || 'fr-DZ'}</p></div>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                  <AlignLeft size={18} className="text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">{p('section_content')}</h3>
                </div>
                <pre className="text-white/80 text-sm whitespace-pre-wrap font-mono bg-black/20 rounded-lg p-4">{selectedRecord.content || p('no_content')}</pre>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Tag size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_variables_short')}</h3>
                  </div>
                  <pre className="text-white/70 text-xs font-mono bg-black/20 rounded-lg p-3 overflow-x-auto">{selectedRecord.variables_json || '[]'}</pre>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <Star size={18} className="text-amber-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_configuration')}</h3>
                  </div>
                  <div className="space-y-3">
                    <div><span className="text-white/60 text-sm">{p('field_status')}</span> <span className="text-white font-semibold ml-2">{selectedRecord.status === 'active' ? p('status_active') : p('status_inactive')}</span></div>
                    <div>
                      {selectedRecord.is_default === 1 ? (
                        <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400 border border-amber-500/30 inline-flex items-center gap-1">
                          <Star size={14} /> {p('default_model')}
                        </span>
                      ) : (
                        <span className="text-white/60 text-sm">{p('custom_model')}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {selectedRecord.notes && (
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="flex items-center gap-2 mb-4 border-b border-white/10 pb-3">
                    <FileText size={18} className="text-purple-400" />
                    <h3 className="text-lg font-semibold text-white">{p('section_notes')}</h3>
                  </div>
                  <p className="text-white/70 text-sm italic">{selectedRecord.notes}</p>
                </div>
              )}
            </div>
        </QawaemErpViewModal>
      ) : null}
    </div>
  );
};

export default ModelesMentionsPage;
